import React, { useState, useRef } from "react"

const SERVICE_OPTIONS = [
  { label: "Photography", value: "photography" },
  { label: "Video", value: "video" },
  { label: "Both", value: "both" },
  { label: "Not sure yet", value: "unsure" },
]

const TIMELINE_OPTIONS = [
  "Within 2 weeks",
  "1-2 months",
  "3+ months",
  "Flexible",
]

const BUDGET_OPTIONS = [
  "Under £2k",
  "£2k-£5k",
  "£5k-£10k",
  "£10k+",
  "Not sure yet",
]

const initialData = {
  service: "",
  brief: "",
  timeline: "",
  budget: "",
  name: "",
  company: "",
  email: "",
  phone: "",
  howFound: "",
}

function Chip({ selected, onClick, children }) {
  return (
    <button
      type="button"
      className="chip"
      aria-pressed={selected}
      data-selected={selected || undefined}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

const STEP_LABELS = ["What you need", "The details", "About you"]
const STEP_HINTS = [
  "Helps me understand the type of work and prepare a relevant quote.",
  "The more context I have, the more accurate the estimate. Nothing here is required.",
  "So I can get back to you with a proper, itemised quote.",
]

export default function QuoteForm() {
  const [step, setStep] = useState(1)
  const [data, setData] = useState(initialData)
  const [errors, setErrors] = useState({})
  const [status, setStatus] = useState("idle")
  const loadedAt = useRef(Date.now())

  const set = (field, value) =>
    setData(prev => ({ ...prev, [field]: value }))

  const validateStep = n => {
    const next = {}
    if (n === 1) {
      if (!data.service)
        next.service = "Pick one so I know the rough shape of the project."
    }
    if (n === 3) {
      if (!data.name.trim()) next.name = "I'll need your name for the quote."
      if (!data.email.trim()) {
        next.email = "I need an email to send the quote to."
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
        next.email = "That doesn't look right. Check for typos?"
      }
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const goNext = () => {
    if (!validateStep(step)) return
    setStep(s => Math.min(s + 1, 3))
  }

  const goBack = () => setStep(s => Math.max(s - 1, 1))

  const handleSubmit = async e => {
    e.preventDefault()
    if (!validateStep(3)) return

    if (data._website_url) {
      setStatus("success")
      return
    }
    if (Date.now() - loadedAt.current < 3000) {
      setStatus("success")
      return
    }

    setStatus("submitting")

    const payload = {
      service: data.service,
      brief: data.brief,
      timeline: data.timeline,
      budget: data.budget,
      contact: {
        name: data.name,
        company: data.company,
        email: data.email,
        phone: data.phone,
        howFound: data.howFound,
      },
      submittedAt: new Date().toISOString(),
      website_url: data._website_url || "",
      loadedAt: new Date(loadedAt.current).toISOString(),
    }

    try {
      const res = await fetch("/.netlify/functions/quote-submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error(`Submit failed with ${res.status}`)
      setStatus("success")
    } catch (err) {
      console.error(err)
      setStatus("error")
    }
  }

  if (status === "success") {
    return (
      <div className="quote-form quote-form--done">
        <span className="service-label">Thanks, {data.name.split(" ")[0]}</span>
        <h2>(Almost) Immediate Estimate</h2>
        <p>
          I'll review everything you've sent and put together an itemised
          estimate. If I need to clarify anything, I'll drop you an email
          first.
        </p>
        <p className="quote-form__done-alt">
          Prefer to talk directly?{" "}
          <a href="mailto:info@gregwoodwardphoto.com">
            info@gregwoodwardphoto.com
          </a>
        </p>
      </div>
    )
  }

  return (
    <form className="quote-form" onSubmit={handleSubmit} noValidate>
      <div className="quote-form__progress">
        <div className="quote-form__progress-bar">
          <div
            className="quote-form__progress-fill"
            style={{ width: `${(step / 3) * 100}%` }}
          />
        </div>
        <div className="quote-form__progress-meta">
          <span className="quote-form__progress-label">
            {step} of 3: {STEP_LABELS[step - 1]}
          </span>
          <span className="quote-form__progress-hint">
            {STEP_HINTS[step - 1]}
          </span>
        </div>
      </div>

      {/* honeypot */}
      <input
        type="text"
        name="website_url"
        tabIndex="-1"
        autoComplete="off"
        value={data._website_url || ""}
        onChange={e => set("_website_url", e.target.value)}
        className="quote-form__honeypot"
        aria-hidden="true"
      />

      {step === 1 && (
        <fieldset className="quote-form__step">
          <legend>What are you looking for?</legend>

          <div className="quote-form__field">
            <span className="quote-form__field-label">I need</span>
            <div className="chip-group">
              {SERVICE_OPTIONS.map(s => (
                <Chip
                  key={s.value}
                  selected={data.service === s.value}
                  onClick={() => set("service", s.value)}
                >
                  {s.label}
                </Chip>
              ))}
            </div>
            {errors.service && (
              <span className="quote-form__error">{errors.service}</span>
            )}
          </div>

        </fieldset>
      )}

      {step === 2 && (
        <fieldset className="quote-form__step">
          <legend>Tell me a bit more</legend>

          <div className="quote-form__field">
            <label className="quote-form__field-label" htmlFor="qf-brief">
              What's the project about?{" "}
              <span className="quote-form__optional">(optional)</span>
            </label>
            <textarea
              id="qf-brief"
              rows="4"
              placeholder="A few lines is plenty. What are you producing, who is it for, and where would it be shot?"
              value={data.brief}
              onChange={e => set("brief", e.target.value)}
            />
          </div>

          <div className="quote-form__field">
            <span className="quote-form__field-label">
              When do you need this?{" "}
              <span className="quote-form__optional">(optional)</span>
            </span>
            <div className="chip-group">
              {TIMELINE_OPTIONS.map(t => (
                <Chip
                  key={t}
                  selected={data.timeline === t}
                  onClick={() => set("timeline", data.timeline === t ? "" : t)}
                >
                  {t}
                </Chip>
              ))}
            </div>
          </div>

          <div className="quote-form__field">
            <span className="quote-form__field-label">
              Budget range{" "}
              <span className="quote-form__optional">(optional)</span>
            </span>
            <div className="chip-group">
              {BUDGET_OPTIONS.map(b => (
                <Chip
                  key={b}
                  selected={data.budget === b}
                  onClick={() => set("budget", data.budget === b ? "" : b)}
                >
                  {b}
                </Chip>
              ))}
            </div>
          </div>
        </fieldset>
      )}

      {step === 3 && (
        <fieldset className="quote-form__step">
          <legend>Where should I send the quote?</legend>

          <div className="quote-form__field">
            <label className="quote-form__field-label" htmlFor="qf-name">
              Name
            </label>
            <input
              id="qf-name"
              type="text"
              value={data.name}
              onChange={e => set("name", e.target.value)}
              onBlur={() => { if (data.name) validateStep(3) }}
            />
            {errors.name && <span className="quote-form__error">{errors.name}</span>}
          </div>

          <div className="quote-form__field">
            <label className="quote-form__field-label" htmlFor="qf-company">
              Company{" "}
              <span className="quote-form__optional">(optional)</span>
            </label>
            <input
              id="qf-company"
              type="text"
              value={data.company}
              onChange={e => set("company", e.target.value)}
            />
          </div>

          <div className="quote-form__field">
            <label className="quote-form__field-label" htmlFor="qf-email">
              Email
            </label>
            <input
              id="qf-email"
              type="email"
              inputMode="email"
              value={data.email}
              onChange={e => set("email", e.target.value)}
              onBlur={() => { if (data.email) validateStep(3) }}
            />
            {errors.email && <span className="quote-form__error">{errors.email}</span>}
          </div>

          <div className="quote-form__field">
            <label className="quote-form__field-label" htmlFor="qf-phone">
              Phone{" "}
              <span className="quote-form__optional">(optional)</span>
            </label>
            <input
              id="qf-phone"
              type="tel"
              inputMode="tel"
              value={data.phone}
              onChange={e => set("phone", e.target.value)}
            />
          </div>

          <div className="quote-form__field">
            <label className="quote-form__field-label" htmlFor="qf-found">
              How did you find me?{" "}
              <span className="quote-form__optional">(optional)</span>
            </label>
            <input
              id="qf-found"
              type="text"
              placeholder="Google, referral, Instagram..."
              value={data.howFound}
              onChange={e => set("howFound", e.target.value)}
            />
          </div>

          {status === "error" && (
            <p className="quote-form__error quote-form__error--banner">
              Something went wrong sending that. Try again, or email{" "}
              <a href="mailto:info@gregwoodwardphoto.com">
                info@gregwoodwardphoto.com
              </a>
              .
            </p>
          )}
        </fieldset>
      )}

      <div className="quote-form__nav">
        {step > 1 ? (
          <button type="button" className="quote-form__back" onClick={goBack}>
            Back
          </button>
        ) : (
          <span />
        )}
        {step < 3 ? (
          <button type="button" className="quote-form__next" onClick={goNext}>
            Continue <span aria-hidden="true">↗</span>
          </button>
        ) : (
          <button
            type="submit"
            className="quote-form__next"
            disabled={status === "submitting"}
          >
            {status === "submitting" ? "Sending..." : "Get my quote"}
          </button>
        )}
      </div>
    </form>
  )
}
